import { ChangeDetectorRef, Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CustomerService } from 'src/app/services/customer.service';
import { BoatService } from 'src/app/services/boat.service';
import { MovementPlanAppointmentService } from 'src/app/services/movements/movement-plan-appointment.service';
import { FranchiseServiceService } from 'src/app/services/franchise/franchise-service.service';
import { BoatFilterListWithCustomerDTO } from 'src/app/models/dtos/boat-filter-list-with-customerDTO';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-movement-select-unified',
  templateUrl: './movement-select-unified.component.html',
  styleUrls: ['./movement-select-unified.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MovementSelectUnifiedComponent),
      multi: true
    }
  ]
})
export class MovementSelectUnifiedComponent implements OnInit, OnChanges, ControlValueAccessor {

  @Input() searchByCustomer: boolean | null = null;
  @Input() boat: BoatFilterListWithCustomerDTO | null = null;
  @Input() form: any;

  allCustomers: any[] = [];
  listBoat: BoatFilterListWithCustomerDTO[] = [];
  unifiedSearchOptions: any[] = [];
  selectedUnifiedOption: string | null = null;
  ownersAuthorizedByBoat: { [boatId: number]: { owners: number[]; authorizeds: number[] } } = {};
  
  disabled = false;

  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(
    private customerService: CustomerService,
    private boatService: BoatService,
    private movementPlanAppointmentService: MovementPlanAppointmentService,
    private franchiseService: FranchiseServiceService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadAllData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchByCustomer'] || changes['boat']) {
      this.loadAllData();
    }
  }

  async loadAllData(): Promise<void> {
    try {
      const ownersAuthorizeds = await new Promise<any>((resolve) => {
        this.customerService.getOwnersAndAuthorizeds().subscribe(
          (response) => resolve(response),
          (error) => {
            console.log('Erro ao carregar owners-and-authorizeds', error);
            resolve(null);
          }
        );
      });

      if (ownersAuthorizeds && Array.isArray(ownersAuthorizeds.boats)) {
        this.ownersAuthorizedByBoat = {};
        ownersAuthorizeds.boats.forEach((b: any) => {
          this.ownersAuthorizedByBoat[b.id] = {
            owners: Array.isArray(b.owners) ? b.owners : [],
            authorizeds: Array.isArray(b.authorizeds) ? b.authorizeds : []
          };
        });
      }

      this.allCustomers = await new Promise<any[]>((resolve) => {
        this.customerService.getAllWithBoats().subscribe(
          (data) => resolve(data.filter((customer) => customer.active)),
          (error) => {
            console.log(error);
            resolve([]);
          }
        );
      });

      this.listBoat = await new Promise<BoatFilterListWithCustomerDTO[]>((resolve) => {
        this.boatService.getBoatFilterListWithCustomer().subscribe(
          (data) => resolve(data.filter((b: BoatFilterListWithCustomerDTO) => b.active)),
          (error) => {
            console.log(error);
            resolve([]);
          }
        );
      });

      this.loadUnifiedSearchOptions();
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  }

  loadUnifiedSearchOptions(): void {
    this.unifiedSearchOptions = [];
    
    if (!this.allCustomers || !this.listBoat || this.allCustomers.length === 0 || this.listBoat.length === 0) {
      return;
    }
    
    const addedCombinations = new Set<string>();
    
    this.allCustomers.forEach(customer => {
      const customerBoats = this.listBoat.filter(boat => 
        boat.customerIds && boat.customerIds.includes(customer.id) && boat.active
      );
      
      customerBoats.forEach(boat => {
        const combinationKey = `${customer.id}_${boat.id}`;
        
        if (!addedCombinations.has(combinationKey)) {
          const roleInfo = this.ownersAuthorizedByBoat[boat.id];
          let roleLabel = '';
          if (roleInfo) {
            if (Array.isArray(roleInfo.owners) && roleInfo.owners.includes(customer.id)) {
              roleLabel = 'Proprietário';
            } else if (Array.isArray(roleInfo.authorizeds) && roleInfo.authorizeds.includes(customer.id)) {
              roleLabel = 'Autorizado';
            }
          }

          const label = `${customer.id} ${customer.name} - ${roleLabel || (customer.accessGroup?.name || '')} / ${boat.name}${boat.maintenance ? ' (Em manutenção)' : ''}`;
          this.unifiedSearchOptions.push({
            label: label,
            value: `customer_${customer.id}_boat_${boat.id}`,
            type: 'customer_boat',
            disabled: boat.maintenance,
            data: {
              customer: customer,
              boat: boat,
              roleLabel: roleLabel
            }
          });
          
          addedCombinations.add(combinationKey);
        }
      });
    });
    
    // Ordenar por label
    this.unifiedSearchOptions.sort((a, b) => a.label.localeCompare(b.label));
  }

  private async validateFranchiseConsumption(boatId: number): Promise<boolean> {
    try {
      const franchiseResponse = await this.franchiseService.getFranchiseConsumptionByBoat(boatId).toPromise();
      console.log('Franchise Consumption Response:', franchiseResponse);

      // The new endpoint returns an array of franchise data
      const franchiseDataArray = Array.isArray(franchiseResponse)
        ? franchiseResponse
        : (franchiseResponse?.data && Array.isArray(franchiseResponse.data) ? franchiseResponse.data : []);

      console.log('Franchise Consumption Data Array:', franchiseDataArray);

      if (franchiseDataArray.length === 0) {
        return true;
      }

      // Filter by boatId since the endpoint may return multiple boats
      const franchiseDataForBoat = franchiseDataArray.filter((item: any) => item.boatId === boatId);
      
      if (franchiseDataForBoat.length === 0) {
        return true;
      }

      // Check each franchise entry for the boat
      for (const franchiseData of franchiseDataForBoat) {
        console.log('Franchise Data for boat:', franchiseData);

        const hasPackage = franchiseData.franchiseGroupName &&
                          franchiseData.franchiseNumber !== null &&
                          franchiseData.franchiseNumber !== undefined;

        if (!hasPackage) {
          continue;
        }

        const franchiseNumber = franchiseData.franchiseNumber || 0;
        const ammountFranchise = franchiseData.ammountFranchise || 0;
        const surplusAmount = franchiseData.surplusAmount || 0;

        console.log('Franchise validation:', { franchiseNumber, ammountFranchise, surplusAmount });

        if (surplusAmount > 0) {
          return await this.showFranchiseWarningDialog('exceeded');
        }
        else if (ammountFranchise === franchiseNumber && franchiseNumber > 0) {
          return await this.showFranchiseWarningDialog('consumedAll');
        }
      }

      return true;
    } catch (error) {
      console.error('Erro ao buscar dados de franquia:', error);
      return true;
    }
  }

  private async showFranchiseWarningDialog(type: 'consumedAll' | 'exceeded'): Promise<boolean> {
    const message = type === 'exceeded'
      ? 'A embarcação já excedeu o limite de franquia, tem certeza que deseja continuar?'
      : 'A embarcação já atingiu a franquia mensal, tem certeza que deseja continuar?';

    await new Promise(resolve => setTimeout(resolve, 300));

    const result = await Swal.fire({
      title: 'Atenção',
      text: message,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      allowOutsideClick: false,
      showCloseButton: true
    });

    return result.isConfirmed;
  }

  async handleUnifiedSelection(): Promise<void> {
    if (!this.selectedUnifiedOption) return;

    const selectedOption = this.unifiedSearchOptions.find(option => option.value === this.selectedUnifiedOption);
    
    if (selectedOption && selectedOption.type === 'customer_boat') {
      try {
        console.log('Opção selecionada:', selectedOption);
        const customerData = selectedOption.data.customer;
        const boatData = selectedOption.data.boat;
        
        // Buscar dados completos do cliente para garantir que temos o federalId e federalIdType
        const fullCustomerData = await this.customerService.getById(customerData.id).toPromise();
        console.log('Dados completos do cliente:', fullCustomerData);
        console.log('Tipo de documento:', fullCustomerData.federalIdType, 'Federal ID:', fullCustomerData.federalId);
        if (fullCustomerData.financialBlock) {
          Swal.fire({
            title: 'Atenção',
            text: 'Cliente possui bloqueio no financeiro.',
            icon: 'warning',
            reverseButtons: true
          });
        }
        
        const fullBoatData = await this.movementPlanAppointmentService.findBoatOptionSelect(boatData.id).toPromise();
        
        // Validar franquia antes de continuar
        const shouldContinue = await this.validateFranchiseConsumption(boatData.id);
        if (!shouldContinue) {
          this.selectedUnifiedOption = null;
          this.cd.detectChanges();
          return;
        }
        
        if (fullBoatData.boatCustomers.find(item => item.blocked)) {
          Swal.fire({
            title: 'Atenção',
            text: 'Embarcação possui bloqueio no financeiro.',
            icon: 'warning',
            reverseButtons: true
          });
        }

        if (this.form) {
          this.form.patchValue({
            customer: fullCustomerData,
            boat: fullBoatData
          }, { emitEvent: true }); 
          
          this.form.get('customer')?.markAsTouched();
          this.form.get('boat')?.markAsTouched();
          
          this.form.updateValueAndValidity();
        }
        
        this.onChange({ customer: fullCustomerData, boat: fullBoatData });
        this.onTouched();
        
        setTimeout(() => {
          this.cd.detectChanges();
        }, 300);
      } catch (error) {
        console.error('Erro ao buscar dados completos:', error);
      }
    }
  }

  changeSelectedUnified(event: any): void {
    this.selectedUnifiedOption = event.value;
    this.handleUnifiedSelection();
  }

  writeValue(value: any): void {
    if (value && value.customer && value.boat) {
      const matchingOption = this.unifiedSearchOptions.find(option => 
        option.data.customer.id === value.customer.id && 
        option.data.boat.id === value.boat.id
      );
      if (matchingOption) {
        this.selectedUnifiedOption = matchingOption.value;
      }
    } else {
      this.selectedUnifiedOption = null;
    }
    this.cd.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}